import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getCommonVendors } from 'utils/apiRequests';
import {
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemValue,
} from './../CategoryDashboardCharts/DashboardChart.styles';
import { FONTSTYLE } from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { renderToString } from 'react-dom/server';
import { getFormattedSpendWithSymbol } from 'utils/number';
import { generateToolTip } from 'utils/filterNames';
import { isNumber } from 'lodash';

const CommonSupplier = createContext();
const pageSize = 15;

const tooltipKeys = {
	commonVendors: 'Vendor Name',
	businessUnits: 'Business Unit',
	totalVendorSpend: 'Total Vendor Spend',
	vendorSpend: 'Vendor Spend',
	aP_L1Category: 'AP L1 Category',
	aP_L2Category: 'AP L2 Category',
	aP_L3Category: 'AP L3 Category',
	aP_L4Category: 'AP L4 Category',
	country: 'Country',
};
const color = [
	'#032E45',
	'#012935',
	'#00212E',
	'#001A27',
	'#00131F',
	'#000C18',
	'#000511',
	'#00020A',
	'#000003',
	'#000000',
];

export const CommonSupplierProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [options, setOptions] = useState({
		colorAxis: {
			minColor: '#FFFFFF',
			maxColor: '#032E45',
			reversed: false,
		},
		chart: {
			type: 'heatmap',
			height: '34%',
		},
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		legend: {
			align: 'right',
			verticalAlign: 'middle',
			layout: 'vertical',
			symbolHeight: 140,
			useHTML: true,
			itemMarginBottom: 50,
			formatter: function() {
				return getFormattedSpendWithSymbol(
					this.value,
					currencySymbol,
					hideDecimals
				).toUpperCase();
			},
			itemStyle: {
				...FONTSTYLE,
				textOverflow: 'ellipsis',
			},
		},

		colors: color,
	});

	const { currentSelectCategory, dateRangeData, isFetching } = useContext(
		CategoryManagementContext
	);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
					};
					getCommonVendors(payload)
						.then(response => {
							if (response.data.length > 0) {
								const sortedData = response.data.sort(
									(a, b) => b.totalVendorSpend - a.totalVendorSpend
								);
								setData(sortedData);
								setIsLoading(false);
							} else {
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[dateRangeData, categoryName, isSettingsFetching, isFetching]
	);

	useEffect(
		() => {
			if (data.length > 0) {
				const maxVendorSpend = Math.max(
					...data.map(dataPt => dataPt.vendorSpend)
				);
				const minVendorSpend = Math.min(
					...data.map(dataPt => dataPt.vendorSpend)
				);
				const transformData = data => {
					return data.reduce((acc, item) => {
						if (!acc[item.commonVendors]) {
							acc[item.commonVendors] = [];
						}
						acc[item.commonVendors].push(item);
						return acc;
					}, {});
				};

				const getBusinessUnitsByVendorSpend = data => {
					const spendByBusinessUnit = data.reduce((acc, curr) => {
						if (acc[curr.businessUnits]) {
							acc[curr.businessUnits] += curr.vendorSpend;
						} else {
							acc[curr.businessUnits] = curr.vendorSpend;
						}
						return acc;
					}, {});

					return Object.entries(spendByBusinessUnit)
						.sort((a, b) => b[1] - a[1])
						.map(entry => entry[0]);
				};

				const listofBusinessUnitBySumOfVendorSpend = getBusinessUnitsByVendorSpend(
					data
				);

				const transformedData = Object.values(transformData(data));
				const firstPageIndex = (currentPage - 1) * pageSize;
				const lastPageIndex = firstPageIndex + pageSize;

				const batchRecords = transformedData
					.slice(firstPageIndex, lastPageIndex)
					.flat();

				const vendorsSet = new Set(
					batchRecords.flat().map(item => item.commonVendors)
				);
				const uniqueVendorsArray = Array.from(vendorsSet);
				const seriesData = batchRecords.map(supplier => {
					return {
						x: listofBusinessUnitBySumOfVendorSpend.indexOf(
							supplier.businessUnits
						),
						y: uniqueVendorsArray.indexOf(supplier.commonVendors),
						value: supplier.vendorSpend,
						colorValue: supplier.vendorSpend,
						tooltip: generateToolTip(supplier),
					};
				});
				const seriesResult = [
					{
						data: seriesData,
						dataLabels: {
							enabled: true,
							color: '#000000',
							formatter: function() {
								return getFormattedSpendWithSymbol(
									this.point.value,
									currencySymbol,
									hideDecimals
								).toUpperCase();
							},
						},
					},
				];

				setOptions(prevState => ({
					...prevState,
					series: seriesResult,
					tooltip: {
						enabled: true,
						useHTML: true,
						formatter: function() {
							const { point } = this;
							const { tooltip } = point;
							return renderToString(
								<TooltipContainer>
									{tooltip.map(item => (
										<TooltipItem key={`${item.name}-${item.value}`}>
											<span>
												<TooltipItemName>
													{tooltipKeys[item.name]}
												</TooltipItemName>
											</span>
											<TooltipItemValue>
												{isNumber(item.value)
													? getFormattedSpendWithSymbol(
															item.value,
															currencySymbol,
															hideDecimals
													  ).toUpperCase()
													: item.value}
											</TooltipItemValue>
										</TooltipItem>
									))}
								</TooltipContainer>
							);
						},
					},
					xAxis: {
						min: 0, // Ensure the axis starts from the first category
						max: listofBusinessUnitBySumOfVendorSpend.length - 1,
						categories: listofBusinessUnitBySumOfVendorSpend,
						opposite: true,
						title: {
							enabled: false,
							text: 'Business Unit',
						},
					},

					yAxis: {
						title: {
							enabled: false,
							text: 'Vendor',
						},
						reversed: true,
						labels: {
							style: {
								...FONTSTYLE,
							},
						},
						categories: uniqueVendorsArray,
					},
					colorAxis: {
						min: minVendorSpend,
						max: maxVendorSpend,
						reversed: false,
					},
					transformDataLength: transformedData.length,
				}));
			}
		}, // eslint-disable-next-line
		[data, currentPage]
	);

	return (
		<CommonSupplier.Provider
			value={{
				isLoading,
				data,
				options,
				currentPage,
				setCurrentPage,
				pageSize,
			}}
		>
			{children}
		</CommonSupplier.Provider>
	);
};

export const useCommonSuppliersContext = () => {
	return useContext(CommonSupplier);
};
